import { ChangeDetectionStrategy, Component, Inject, Injector, OnInit } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamType } from "@common/ADAPT.Common.Model/organisation/team-type";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { emptyIfUndefinedOrNull } from "@common/lib/utilities/rxjs-utilities";
import { TEAM_CONFIGURATION_PAGE } from "@common/page-route-providers";
import { NavigationHierarchyService } from "@common/route/navigation-hierarchy.service";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { UserService } from "@common/user/user.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { LayoutService } from "@common/ux/layout/layout.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { JourneyProgressAuthService } from "@org-common/lib/journey-progress/journey-progress-auth.service";
import { KanbanAuthService } from "@org-common/lib/kanban/kanban-auth.service";
import { LabellingService } from "@org-common/lib/labelling/labelling.service";
import { ObjectivesAuthService } from "@org-common/lib/objectives/objectives-auth.service";
import { OrganisationAuthService } from "@org-common/lib/organisation/organisation-auth.service";
import { PeerCatchupAuthService } from "@org-common/lib/peer-catchup/peer-catchup-auth.service";
import { CommonPurposeVisionAuthService } from "@org-common/lib/purpose-vision/common-purpose-vision-auth.service";
import { TeamAssessmentAuthService } from "@org-common/lib/survey/team-assessment/team-assessment-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { CommonTeamsUiService } from "@org-common/lib/teams/common-teams-ui.service";
import { ValuesConstitutionAuthService } from "@org-common/lib/values-constitution/values-constitution-auth.service";
import { VisionAuthService } from "@org-common/lib/vision/vision-auth.service";
import { IntegratedArchitectureFrameworkAuthService } from "app/features/architecture/integrated-architecture/integrated-architecture-framework-auth.service";
import { SystemisationService } from "app/features/systemisation/systemisation.service";
import { teamSystemsPageRoute } from "app/features/systemisation/team-systems-page/team-systems-page.component";
import { combineLatest, lastValueFrom, Observable, of, ReplaySubject } from "rxjs";
import { filter, first, map, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-team-dashboard-page",
    templateUrl: "./team-dashboard-page.component.html",
    styleUrl: "./team-dashboard-page.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class TeamDashboardPageComponent extends BaseRoutedComponent implements OnInit {
    public readonly TeamType = TeamType;
    public readonly PlatformStandardRead = AuthorisationService.PlatformStandardRead;

    public readonly ConfigureTeam = CommonTeamsAuthService.ConfigureTeam;
    public readonly ReadTeam = CommonTeamsAuthService.ReadTeam;
    public readonly ReadAllPeerCatchups = PeerCatchupAuthService.ReadAllPeerCatchups;
    public readonly ReadObjectivesForTeam = ObjectivesAuthService.ReadObjectivesForTeam;
    public readonly ReadPurposeVision = CommonPurposeVisionAuthService.ReadPurposeVision;
    public readonly ReadSystem = IntegratedArchitectureFrameworkAuthService.ReadSystem;
    public readonly ReadTeamAssessment = TeamAssessmentAuthService.ReadTeamAssessment;
    // use readTeamMeetings here instead of viewTeamMeetings as the latter will be granted to person previously participated in a meeting in this team
    // (which is supposed to be used to evaluate if the meetings nav node is shown for the team)
    public readonly ReadTeamMeetings = CommonTeamsAuthService.ReadTeamMeetings;
    public readonly ReadValuesConstitution = ValuesConstitutionAuthService.ReadValuesConstitution;
    public readonly ViewExternalDashboard = OrganisationAuthService.ViewExternalDashboard;
    public readonly ReadVision = VisionAuthService.ReadVision;
    public readonly ReadJourneyProgress = JourneyProgressAuthService.ReadJourneyProgress;

    public teamId$ = new ReplaySubject<number | undefined>(1);
    public team$: Observable<Team | undefined>;
    public teamConfigureLink$: Observable<string>;

    public canEdit = false;
    public hasStats = true;
    public hasSystems = false;

    public systemsUrl$?: Observable<string>;

    // permissions for each of the dashboard elements

    private person$ = this.userService.currentPerson$;

    constructor(
        teamsService: CommonTeamsService,
        authService: AuthorisationService,
        kanbanAuthService: KanbanAuthService,
        private teamsUiService: CommonTeamsUiService,
        injector: Injector,
        labellingService: LabellingService,
        layoutService: LayoutService,
        private userService: UserService,
        private navHierarchyService: NavigationHierarchyService,
        private systemisationService: SystemisationService,
        @Inject(TEAM_CONFIGURATION_PAGE) teamConfigurationPageRoute: IAdaptRoute<{ teamId: number }>,
    ) {
        super(injector);

        // prime labels for toolbar title
        this.teamId$.pipe(
            emptyIfUndefinedOrNull(),
            switchMap((teamId) => labellingService.getLabelLocationsForTeam(teamId)),
            this.takeUntilDestroyed(),
        ).subscribe();

        this.team$ = this.teamId$.pipe(
            switchMap((teamId) => teamId ? teamsService.getTeamById(teamId) : of(undefined)),
            tap((team) => {
                if (!team) {
                    this.routeService.gotoHome();
                }
            }),
            // prime the layout for the dashboard
            switchMap((team) => team?.layoutId
                ? layoutService.getLayoutById(team.layoutId).pipe(map(() => team))
                : of(team)),
            // prime systems. we need to do this before the dashboard element shows.
            switchMap(async (team) => {
                if (team && await authService.promiseToGetHasAccess(IntegratedArchitectureFrameworkAuthService.ReadSystem, team)) {
                    const systems = await lastValueFrom(this.systemisationService.getSystemsForTeam(team));
                    this.hasSystems = systems.length > 0;
                }

                return team;
            }),
            this.takeUntilDestroyed(),
        );

        this.systemsUrl$ = this.team$.pipe(
            emptyIfUndefinedOrNull(),
            switchMap(({ teamId }) => teamSystemsPageRoute.getRoute({ teamId })),
            this.takeUntilDestroyed(),
        );

        this.teamConfigureLink$ = this.team$.pipe(
            emptyIfUndefinedOrNull(),
            switchMap(({ teamId }) => teamConfigurationPageRoute.getRoute({ teamId })),
            this.takeUntilDestroyed(),
        );

        combineLatest([this.person$, this.team$]).pipe(
            filter(([person]) => !!person),
            this.takeUntilDestroyed(),
        ).subscribe(([person, team]) => {
            this.hasStats = !!team && !!person && kanbanAuthService.personCanReadNonPublicBoardsForTeam(person, team);
        });

        this.team$.pipe(
            switchMap((team) => authService.getHasAccess(CommonTeamsAuthService.ConfigureTeam, team)),
            this.takeUntilDestroyed(),
        ).subscribe((canEdit) => this.canEdit = canEdit);

        this.notifyActivated();
    }

    public async ngOnInit() {
        await this.updateData();
        // component is not reloaded even with onSameUrlNavigation: 'reload'
        // -> so whatever initialized in ngOnInit() which usually retrieve url/query params
        // won't be refreshed when changing from team page to another team page.
        // try this workaround: ngOnInit() is called after NavigationEnd
        //  - register callback for this initialization after navigation end
        // This is to update after switching from team dashboard to another team dashboard (navigationEnd has takeUntilDestoryed in there)
        this.navigationEnd.subscribe(() => this.updateData());
    }

    private async updateData() {
        const teamId = this.getRouteParamInt("teamId");
        this.teamId$.next(teamId);

        this.navHierarchyService.updateActiveNodeFromUrl();
        this.notifyActivated();
    }

    @Autobind
    public viewActivityFeed() {
        return this.team$.pipe(
            first(),
            emptyIfUndefinedOrNull(),
            switchMap((team) => this.teamsUiService.viewActivityFeed(team)),
        );
    }

    @Autobind
    public reactivateTeam() {
        return this.team$.pipe(
            first(),
            emptyIfUndefinedOrNull(),
            switchMap((team) => this.teamsUiService.reactivateTeam(team)),
        );
    }
}
